import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import UserLayout from './UserLayout';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [flashImageFile, setFlashImageFile] = useState(null);
  const [flashImagePreview, setFlashImagePreview] = useState('');
  const [uploadingFlash, setUploadingFlash] = useState(false);
  const [flashMessage, setFlashMessage] = useState({ type: '', text: '' });
  const [showFeedbacks, setShowFeedbacks] = useState(false);
  const [activeTab, setActiveTab] = useState('responses'); // 'responses' or 'feedbacks'
  const [stats, setStats] = useState({
    totalProfiles: 0,
    activeCampaigns: 0,
    totalSurveys: 0,
    profileViews: 0,
    totalFeedbacks: 0,
    engagementRate: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !user) return;

        setLoadingStats(true);
        const response = await fetch(`${API_BASE_URL}/api/profiles/dashboard/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setStats(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchDashboardStats();
    // Refresh stats every 30 seconds for real-time updates
    const interval = setInterval(fetchDashboardStats, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Check URL hash to set active tab
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#responses') {
        setActiveTab('responses');
      } else if (hash === '#feedbacks') {
        setActiveTab('feedbacks');
      } else {
        // Default to responses if no valid hash or other hash
        setActiveTab('responses');
        if (!hash || (hash !== '#responses' && hash !== '#feedbacks')) {
          // Don't set hash automatically, let user navigate
        }
      }
    };

    // Set initial tab based on hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Fetch user's profile and survey responses
  useEffect(() => {
    const fetchProfileAndResponses = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !user) return;

        // Fetch user's profile
        const profileResponse = await fetch(`${API_BASE_URL}/api/profiles/username/${user.username}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
            if (profileData.success && profileData.data) {
            const profileDataParsed = {
              ...profileData.data,
              feedbacks: Array.isArray(profileData.data.feedbacks) 
                ? profileData.data.feedbacks 
                : (typeof profileData.data.feedbacks === 'string' 
                    ? JSON.parse(profileData.data.feedbacks || '[]') 
                    : [])
            };
            
            // Debug: Log feedbacks to check image field
            console.log('Feedbacks loaded:', profileDataParsed.feedbacks);
            profileDataParsed.feedbacks.forEach((fb, idx) => {
              console.log(`Feedback ${idx}:`, {
                name: fb.name,
                hasImage: !!fb.image,
                image: fb.image,
                imageType: typeof fb.image
              });
            });
            
            setProfile(profileDataParsed);
            // Set flash image preview if exists
            if (profileData.data.flash_image) {
              setFlashImagePreview(`${API_BASE_URL}${profileData.data.flash_image}`);
            }
            
            // Fetch survey responses for this profile
            setLoadingResponses(true);
            const responsesResponse = await fetch(`${API_BASE_URL}/api/survey-responses/profile/${profileData.data.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (responsesResponse.ok) {
              const responsesData = await responsesResponse.json();
              if (responsesData.success) {
                setSurveyResponses(responsesData.data || []);
              }
            }
            setLoadingResponses(false);
          }
        }
      } catch (error) {
        console.error('Error fetching profile/responses:', error);
        setLoadingResponses(false);
      }
    };

    fetchProfileAndResponses();
  }, [user]);

  const recentActivity = [
    { id: 1, action: 'Profile Updated', time: '2 hours ago', type: 'update' },
    { id: 2, action: 'New Campaign Created', time: '5 hours ago', type: 'create' },
    { id: 3, action: 'Profile Viewed', time: '1 day ago', type: 'view' },
    { id: 4, action: 'Logo Uploaded', time: '2 days ago', type: 'upload' }
  ];

  return (
    <UserLayout pageTitle="Dashboard" pageSubtitle="Welcome to your political profile dashboard">
      <div style={{ padding: '24px' }}>

        {/* Summary Cards */}
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          {/* Total Profiles Card */}
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '20px', padding: '28px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '8px' }}>Total Profiles</h3>
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', margin: 0 }}>
                  {loadingStats ? '...' : stats.totalProfiles}
                </h2>
              </div>
              <div style={{ fontSize: '3rem', opacity: 0.3 }}>📋</div>
            </div>
            <Link to="/create-profile" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.85rem', opacity: 0.9 }}>Create new profile →</Link>
          </div>

          {/* Active Campaigns Card */}
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '20px', padding: '28px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '8px' }}>Active Campaigns</h3>
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', margin: 0 }}>
                  {loadingStats ? '...' : stats.activeCampaigns}
                </h2>
              </div>
              <div style={{ fontSize: '3rem', opacity: 0.3 }}>🎯</div>
            </div>
            <a href="#responses" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.85rem', opacity: 0.9 }}>View responses →</a>
          </div>

          {/* Total Survey Card */}
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', borderRadius: '20px', padding: '28px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '8px' }}>Total Survey</h3>
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', margin: 0 }}>
                  {loadingStats ? '...' : stats.totalSurveys}
                </h2>
              </div>
              <div style={{ fontSize: '3rem', opacity: 0.3 }}>📊</div>
            </div>
            <a href="#responses" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.85rem', opacity: 0.9 }}>View surveys →</a>
          </div>

          {/* Engagement Rate Card */}
          <div className="stat-card" style={{ background: '#fff', borderRadius: '20px', padding: '28px', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>Engagement Rate</h3>
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <div style={{ fontSize: '3rem', fontWeight: '700', color: '#10b981', marginBottom: '8px' }}>
                {loadingStats ? '...' : `${stats.engagementRate}%`}
              </div>
              <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Overall Engagement</p>
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '24px' }}>
          {/* Weekly Activity */}
          <div className="chart-card" style={{ background: '#fff', borderRadius: '20px', padding: '28px', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '24px' }}>Weekly Activity</h3>
            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '12px', color: '#64748b' }}>
              <p>Activity Chart</p>
            </div>
          </div>

          {/* Recent Activity Table */}
          <div className="chart-card" style={{ background: '#fff', borderRadius: '20px', padding: '28px', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '600' }}>Recent Activity</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button style={{ background: '#f1f5f9', color: '#1e293b', border: '1px solid #e2e8f0', padding: '6px 16px', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' }}>Filter</button>
                <button style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' }}>Download</button>
              </div>
            </div>
            <div className="activity-list">
              {recentActivity.map((activity) => (
                <div key={activity.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <div>
                    <p style={{ color: '#1e293b', marginBottom: '4px', fontWeight: '500' }}>{activity.action}</p>
                    <p style={{ color: '#64748b', fontSize: '0.85rem' }}>{activity.time}</p>
                  </div>
                  <span style={{ 
                    padding: '4px 12px', 
                    borderRadius: '12px', 
                    fontSize: '0.75rem',
                    background: activity.type === 'create' ? '#10b981' : activity.type === 'update' ? '#3b82f6' : '#f59e0b',
                    color: '#fff'
                  }}>
                    {activity.type}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Visitors Stats */}
          <div className="chart-card" style={{ background: '#fff', borderRadius: '20px', padding: '28px', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>Profile Views</h3>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>
                {loadingStats ? '...' : stats.profileViews.toLocaleString()}
              </h2>
              <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', marginTop: '12px', overflow: 'hidden' }}>
                <div style={{ 
                  height: '100%', 
                  width: `${Math.min((stats.profileViews / Math.max(stats.profileViews + 100, 1)) * 100, 100)}%`, 
                  background: '#3b82f6', 
                  borderRadius: '4px' 
                }}></div>
              </div>
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>Feedbacks</h3>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>
                {loadingStats ? '...' : stats.totalFeedbacks}
              </h2>
              <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', marginTop: '12px', overflow: 'hidden' }}>
                <div style={{ 
                  height: '100%', 
                  width: `${Math.min((stats.totalFeedbacks / Math.max(stats.totalFeedbacks + 10, 1)) * 100, 100)}%`, 
                  background: '#10b981', 
                  borderRadius: '4px' 
                }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Flash Image Section */}
        {profile && (
          <div 
            className="chart-card" 
            style={{ 
              background: '#fff', 
              borderRadius: '20px', 
              padding: '28px', 
              color: '#1e293b', 
              marginTop: '32px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                  ⚡ Flash Image (Banner/Popup)
                </h3>
              </div>

              {flashMessage.text && (
                <div style={{
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  background: flashMessage.type === 'success' ? '#10b981' : '#ef4444',
                  color: '#fff',
                  fontSize: '0.9rem'
                }}>
                  {flashMessage.text}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Current Flash Image Preview */}
                {flashImagePreview && (
                  <div>
                    <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '12px' }}>Current Flash Image:</p>
                    <div style={{
                      width: '100%',
                      maxHeight: '300px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '2px solid #e2e8f0',
                      background: '#f8fafc'
                    }}>
                      <img 
                        src={flashImagePreview} 
                        alt="Flash Image" 
                        style={{ 
                          width: '100%', 
                          height: 'auto', 
                          display: 'block',
                          objectFit: 'contain'
                        }} 
                      />
                    </div>
                  </div>
                )}

                {/* Upload Flash Image */}
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '12px' }}>Upload New Flash Image:</p>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Validate file type
                          if (!file.type.match('image.*')) {
                            setFlashMessage({ type: 'error', text: 'Please select an image file' });
                            return;
                          }
                          
                          // Validate file size (5MB)
                          if (file.size > 5 * 1024 * 1024) {
                            setFlashMessage({ type: 'error', text: 'File size should be less than 5MB' });
                            return;
                          }

                          setFlashImageFile(file);
                          
                          // Create preview
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFlashImagePreview(reader.result);
                          };
                          reader.readAsDataURL(file);
                          setFlashMessage({ type: '', text: '' });
                        }
                      }}
                      style={{ display: 'none' }}
                      id="flash-image-input"
                    />
                    <label
                      htmlFor="flash-image-input"
                      style={{
                        padding: '10px 20px',
                        background: '#3b82f6',
                        color: '#fff',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        border: 'none',
                        display: 'inline-block'
                      }}
                    >
                      📁 Choose Image
                    </label>
                    {flashImageFile && (
                      <button
                        onClick={async () => {
                          if (!flashImageFile) {
                            setFlashMessage({ type: 'error', text: 'Please select an image file' });
                            return;
                          }

                          setUploadingFlash(true);
                          setFlashMessage({ type: '', text: '' });

                          try {
                            const token = localStorage.getItem('token');
                            const formData = new FormData();
                            formData.append('image', flashImageFile);

                            const response = await fetch(`${API_BASE_URL}/api/upload/flash`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`
                              },
                              body: formData
                            });

                            const data = await response.json();

                            if (response.ok && data.success) {
                              const flashImageUrl = `${API_BASE_URL}${data.data.url}`;
                              
                              // Update profile with flash image
                              const updateResponse = await fetch(`${API_BASE_URL}/api/profiles/${profile.id}`, {
                                method: 'PUT',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  ...profile,
                                  flash_image: data.data.url
                                })
                              });

                              if (updateResponse.ok) {
                                setFlashMessage({ type: 'success', text: 'Flash image uploaded and saved successfully!' });
                                setFlashImageFile(null);
                                // Refresh profile
                                const profileResponse = await fetch(`${API_BASE_URL}/api/profiles/username/${user.username}`, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });
                                if (profileResponse.ok) {
                                  const profileData = await profileResponse.json();
                                  if (profileData.success && profileData.data) {
                                    setProfile(profileData.data);
                                    setFlashImagePreview(`${API_BASE_URL}${profileData.data.flash_image}`);
                                  }
                                }
                              } else {
                                setFlashMessage({ type: 'error', text: 'Image uploaded but failed to save to profile' });
                              }
                            } else {
                              setFlashMessage({ type: 'error', text: data.message || 'Error uploading flash image' });
                            }
                          } catch (error) {
                            console.error('Error uploading flash image:', error);
                            setFlashMessage({ type: 'error', text: 'Error uploading flash image. Please try again.' });
                          } finally {
                            setUploadingFlash(false);
                          }
                        }}
                        disabled={uploadingFlash}
                        style={{
                          padding: '10px 20px',
                          background: uploadingFlash ? '#64748b' : '#3b82f6',
                          color: '#fff',
                          borderRadius: '8px',
                          cursor: uploadingFlash ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          border: 'none'
                        }}
                      >
                        {uploadingFlash ? '⏳ Uploading...' : '⬆️ Upload & Save'}
                      </button>
                    )}
                    {flashImageFile && (
                      <button
                        onClick={() => {
                          setFlashImageFile(null);
                          setFlashImagePreview(profile?.flash_image ? `${API_BASE_URL}${profile.flash_image}` : '');
                          setFlashMessage({ type: '', text: '' });
                        }}
                        style={{
                          padding: '10px 20px',
                          background: '#ef4444',
                          color: '#fff',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          border: 'none'
                        }}
                      >
                        ❌ Cancel
                      </button>
                    )}
                  </div>
                  {flashImageFile && (
                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px' }}>
                      Selected: {flashImageFile.name} ({(flashImageFile.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>

                <div style={{
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                    💡 <strong>Note:</strong> Flash image is a banner/popup image that appears prominently on your profile page. 
                    Recommended size: 1200x400px or similar banner dimensions.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Combined Responses & Feedbacks Section with Tabs */}
        {profile && (window.location.hash === '#responses' || window.location.hash === '#feedbacks') && (
          <div 
            id="responses"
            className="chart-card" 
            style={{ 
              background: '#fff', 
              borderRadius: '20px', 
              padding: '28px', 
              color: '#1e293b', 
              marginTop: '32px',
              scrollMarginTop: '100px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            {/* Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '24px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '12px'
            }}>
              <button
                onClick={() => {
                  setActiveTab('responses');
                  window.location.hash = '#responses';
                }}
                style={{
                  padding: '12px 24px',
                  background: activeTab === 'responses' ? '#3b82f6' : 'transparent',
                  color: activeTab === 'responses' ? '#fff' : '#64748b',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: activeTab === 'responses' ? '600' : '500',
                  transition: 'all 0.3s',
                  borderBottom: activeTab === 'responses' ? '3px solid #3b82f6' : '3px solid transparent',
                  marginBottom: '-12px'
                }}
              >
                📋 Survey Responses ({surveyResponses.length})
              </button>
              <button
                onClick={() => {
                  setActiveTab('feedbacks');
                  window.location.hash = '#feedbacks';
                }}
                style={{
                  padding: '12px 24px',
                  background: activeTab === 'feedbacks' ? '#3b82f6' : 'transparent',
                  color: activeTab === 'feedbacks' ? '#fff' : '#64748b',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: activeTab === 'feedbacks' ? '600' : '500',
                  transition: 'all 0.3s',
                  borderBottom: activeTab === 'feedbacks' ? '3px solid #3b82f6' : '3px solid transparent',
                  marginBottom: '-12px'
                }}
              >
                💬 Feedbacks ({profile.feedbacks && Array.isArray(profile.feedbacks) ? profile.feedbacks.length : 0})
              </button>
            </div>

              {/* Survey Responses Tab Content */}
              {activeTab === 'responses' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                      📋 Survey Responses ({surveyResponses.length})
                    </h3>
                    <button 
                  onClick={() => {
                    // Refresh responses
                    const fetchResponses = async () => {
                      try {
                        const token = localStorage.getItem('token');
                        setLoadingResponses(true);
                        const responsesResponse = await fetch(`${API_BASE_URL}/api/survey-responses/profile/${profile.id}`, {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        if (responsesResponse.ok) {
                          const responsesData = await responsesResponse.json();
                          if (responsesData.success) {
                            setSurveyResponses(responsesData.data || []);
                          }
                        }
                        setLoadingResponses(false);
                      } catch (error) {
                        console.error('Error fetching responses:', error);
                        setLoadingResponses(false);
                      }
                    };
                    fetchResponses();
                  }}
                  style={{ 
                    background: '#3b82f6', 
                    color: '#fff', 
                    border: 'none', 
                    padding: '6px 16px', 
                    borderRadius: '8px', 
                    fontSize: '0.85rem', 
                    cursor: 'pointer' 
                  }}
                >
                    🔄 Refresh
                  </button>
                </div>

                {loadingResponses ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  Loading responses...
                </div>
              ) : surveyResponses.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <p style={{ fontSize: '1rem', marginBottom: '8px' }}>No survey responses yet</p>
                  <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>Responses will appear here when users submit your surveys</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '600px', overflowY: 'auto' }}>
                  {surveyResponses.map((response, index) => (
                    <div
                      key={response.id || index}
                      style={{
                        background: '#f8fafc',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                            {response.survey_title}
                          </h4>
                          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '8px' }}>
                            Survey #{response.survey_index + 1}
                          </p>
                          {response.created_at && (
                            <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              📅 {new Date(response.created_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {response.respondent_name && (
                          <div style={{
                            padding: '6px 12px',
                            background: '#3b82f6',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            color: '#fff'
                          }}>
                            👤 {response.respondent_name}
                          </div>
                        )}
                      </div>

                      {/* Display Responses */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                        {response.responses && typeof response.responses === 'object' && Object.keys(response.responses).map((qIndex) => {
                          const qResponse = response.responses[qIndex];
                          return (
                            <div
                              key={qIndex}
                              style={{
                                padding: '12px',
                                background: '#f8fafc',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0'
                              }}
                            >
                              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#cbd5e1', marginBottom: '6px' }}>
                                {qResponse.question || `Question ${parseInt(qIndex) + 1}`}
                              </p>
                              <p style={{ fontSize: '0.95rem', color: '#fff', margin: 0 }}>
                                {qResponse.answer || 'No answer provided'}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </div>
              )}

              {/* Feedbacks Tab Content */}
              {activeTab === 'feedbacks' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                      💬 Feedbacks ({profile.feedbacks && Array.isArray(profile.feedbacks) ? profile.feedbacks.length : 0})
                    </h3>
                  </div>

                  <div>
                  {!profile.feedbacks || !Array.isArray(profile.feedbacks) || profile.feedbacks.length === 0 ? (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      background: '#0f172a',
                      borderRadius: '12px',
                      border: '1px solid #334155'
                    }}>
                      <p style={{ fontSize: '1rem', color: '#94a3b8', margin: 0 }}>
                        📭 No feedbacks received yet. Share your profile to get feedbacks!
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {profile.feedbacks.map((feedback, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '20px',
                            background: '#0f172a',
                            borderRadius: '12px',
                            border: '1px solid #334155',
                            display: 'flex',
                            gap: '16px',
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#3b82f6';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#334155';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {(() => {
                            // Check if image exists and is valid
                            if (!feedback.image) {
                              console.log(`Feedback ${index}: No image field`);
                              return null;
                            }
                            
                            // Handle different image formats - can be string (single) or array (multiple)
                            let imageUrls = [];
                            
                            if (Array.isArray(feedback.image)) {
                              // Multiple images
                              imageUrls = feedback.image.filter(img => img && img.trim() !== '' && img !== 'null' && img !== 'undefined');
                            } else if (typeof feedback.image === 'string') {
                              const trimmedImage = feedback.image.trim();
                              
                              if (trimmedImage === '' || trimmedImage === 'null' || trimmedImage === 'undefined') {
                                console.log(`Feedback ${index}: Image is empty/null string`);
                                return null;
                              }
                              
                              // Try to parse as JSON array
                              try {
                                const parsed = JSON.parse(trimmedImage);
                                if (Array.isArray(parsed)) {
                                  imageUrls = parsed.filter(img => img && img.trim() !== '' && img !== 'null' && img !== 'undefined');
                                } else {
                                  imageUrls = [trimmedImage];
                                }
                              } catch (e) {
                                // Not JSON, treat as single URL
                                imageUrls = [trimmedImage];
                              }
                            } else {
                              console.log(`Feedback ${index}: Image is not a string or array, type:`, typeof feedback.image);
                              return null;
                            }
                            
                            if (imageUrls.length === 0) {
                              return null;
                            }
                            
                            // Process URLs - add API_BASE_URL if needed
                            const processedUrls = imageUrls.map(img => {
                              if (typeof img === 'string') {
                                const trimmed = img.trim();
                                if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                                  return trimmed;
                                } else {
                                  return `${API_BASE_URL}${trimmed.startsWith('/') ? trimmed : '/' + trimmed}`;
                                }
                              }
                              return img;
                            });
                            
                            console.log(`Feedback ${index}: Displaying ${processedUrls.length} image(s) -`, {
                              original: feedback.image,
                              processedUrls: processedUrls,
                              apiBase: API_BASE_URL
                            });
                            
                            return (
                              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {processedUrls.map((imageUrl, imgIndex) => (
                                  <img
                                    key={imgIndex}
                                    src={imageUrl}
                                    alt={`Feedback ${index + 1} - Image ${imgIndex + 1}`}
                                    style={{
                                      width: '120px',
                                      height: '120px',
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      border: '1px solid #334155',
                                      cursor: 'pointer',
                                      backgroundColor: '#1e293b'
                                    }}
                                    onClick={() => {
                                      window.open(imageUrl, '_blank');
                                    }}
                                    onError={(e) => {
                                      console.error(`Error loading feedback ${index} image ${imgIndex}:`, {
                                        originalUrl: imageUrl,
                                        apiBaseUrl: API_BASE_URL,
                                        error: 'Image failed to load'
                                      });
                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iIzFlMjkzYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NDc0OGIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                                    }}
                                    onLoad={() => {
                                      console.log(`Feedback ${index}: Image ${imgIndex} loaded successfully:`, imageUrl);
                                    }}
                                  />
                                ))}
                              </div>
                            );
                          })()}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                              <div>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                  Name
                                </p>
                                <p style={{ fontSize: '1rem', color: '#fff', margin: 0, fontWeight: '600' }}>
                                  {feedback.name}
                                </p>
                              </div>
                              <div>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                  Mobile Number
                                </p>
                                <p style={{ fontSize: '1rem', color: '#cbd5e1', margin: 0 }}>
                                  {feedback.mobile_number}
                                </p>
                              </div>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                Address
                              </p>
                              <p style={{ fontSize: '0.95rem', color: '#cbd5e1', margin: 0 }}>
                                {feedback.address}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                Feedback
                              </p>
                              <p style={{ fontSize: '0.95rem', color: '#fff', margin: 0, lineHeight: '1.6' }}>
                                {feedback.description}
                              </p>
                            </div>
                            {feedback.submitted_at && (
                              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #334155' }}>
                                <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>
                                  📅 Submitted: {new Date(feedback.submitted_at).toLocaleDateString('en-IN', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </div>
              )}
            </div>
          )}

          {/* Old Feedbacks Section - Hidden when tabs are active */}
          {profile && !(window.location.hash === '#responses' || window.location.hash === '#feedbacks') && (
            <div 
              id="feedbacks"
              className="chart-card" 
              style={{ 
                background: '#1e293b', 
                borderRadius: '20px', 
                padding: '28px', 
                color: '#fff', 
                marginTop: '32px',
                scrollMarginTop: '100px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                  💬 Feedbacks ({profile.feedbacks && Array.isArray(profile.feedbacks) ? profile.feedbacks.length : 0})
                </h3>
                {!showFeedbacks && (
                  <button
                    onClick={() => setShowFeedbacks(true)}
                    style={{
                      padding: '8px 16px',
                      background: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    View Feedbacks
                  </button>
                )}
                {showFeedbacks && (
                  <button
                    onClick={() => setShowFeedbacks(false)}
                    style={{
                      padding: '8px 16px',
                      background: '#64748b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    Hide Feedbacks
                  </button>
                )}
              </div>

              {showFeedbacks && (
                <div>
                  {!profile.feedbacks || !Array.isArray(profile.feedbacks) || profile.feedbacks.length === 0 ? (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      background: '#0f172a',
                      borderRadius: '12px',
                      border: '1px solid #334155'
                    }}>
                      <p style={{ fontSize: '1rem', color: '#94a3b8', margin: 0 }}>
                        📭 No feedbacks received yet. Share your profile to get feedbacks!
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {profile.feedbacks.map((feedback, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '20px',
                            background: '#0f172a',
                            borderRadius: '12px',
                            border: '1px solid #334155',
                            display: 'flex',
                            gap: '16px',
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#3b82f6';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#334155';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {(() => {
                            // Check if image exists and is valid
                            if (!feedback.image) {
                              console.log(`Feedback ${index}: No image field`);
                              return null;
                            }
                            
                            // Handle different image formats - can be string (single) or array (multiple)
                            let imageUrls = [];
                            
                            if (Array.isArray(feedback.image)) {
                              // Multiple images
                              imageUrls = feedback.image.filter(img => img && img.trim() !== '' && img !== 'null' && img !== 'undefined');
                            } else if (typeof feedback.image === 'string') {
                              const trimmedImage = feedback.image.trim();
                              
                              if (trimmedImage === '' || trimmedImage === 'null' || trimmedImage === 'undefined') {
                                console.log(`Feedback ${index}: Image is empty/null string`);
                                return null;
                              }
                              
                              // Try to parse as JSON array
                              try {
                                const parsed = JSON.parse(trimmedImage);
                                if (Array.isArray(parsed)) {
                                  imageUrls = parsed.filter(img => img && img.trim() !== '' && img !== 'null' && img !== 'undefined');
                                } else {
                                  imageUrls = [trimmedImage];
                                }
                              } catch (e) {
                                // Not JSON, treat as single URL
                                imageUrls = [trimmedImage];
                              }
                            } else {
                              console.log(`Feedback ${index}: Image is not a string or array, type:`, typeof feedback.image);
                              return null;
                            }
                            
                            if (imageUrls.length === 0) {
                              return null;
                            }
                            
                            // Process URLs - add API_BASE_URL if needed
                            const processedUrls = imageUrls.map(img => {
                              if (typeof img === 'string') {
                                const trimmed = img.trim();
                                if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                                  return trimmed;
                                } else {
                                  return `${API_BASE_URL}${trimmed.startsWith('/') ? trimmed : '/' + trimmed}`;
                                }
                              }
                              return img;
                            });
                            
                            console.log(`Feedback ${index}: Displaying ${processedUrls.length} image(s) -`, {
                              original: feedback.image,
                              processedUrls: processedUrls,
                              apiBase: API_BASE_URL
                            });
                            
                            return (
                              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {processedUrls.map((imageUrl, imgIndex) => (
                                  <img
                                    key={imgIndex}
                                    src={imageUrl}
                                    alt={`Feedback ${index + 1} - Image ${imgIndex + 1}`}
                                    style={{
                                      width: '120px',
                                      height: '120px',
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      border: '1px solid #334155',
                                      cursor: 'pointer',
                                      backgroundColor: '#1e293b'
                                    }}
                                    onClick={() => {
                                      window.open(imageUrl, '_blank');
                                    }}
                                    onError={(e) => {
                                      console.error(`Error loading feedback ${index} image ${imgIndex}:`, {
                                        originalUrl: imageUrl,
                                        apiBaseUrl: API_BASE_URL,
                                        error: 'Image failed to load'
                                      });
                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iIzFlMjkzYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NDc0OGIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                                    }}
                                    onLoad={() => {
                                      console.log(`Feedback ${index}: Image ${imgIndex} loaded successfully:`, imageUrl);
                                    }}
                                  />
                                ))}
                              </div>
                            );
                          })()}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                              <div>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                  Name
                                </p>
                                <p style={{ fontSize: '1rem', color: '#fff', margin: 0, fontWeight: '600' }}>
                                  {feedback.name}
                                </p>
                              </div>
                              <div>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                  Mobile Number
                                </p>
                                <p style={{ fontSize: '1rem', color: '#cbd5e1', margin: 0 }}>
                                  {feedback.mobile_number}
                                </p>
                              </div>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                Address
                              </p>
                              <p style={{ fontSize: '0.95rem', color: '#cbd5e1', margin: 0 }}>
                                {feedback.address}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 4px 0', fontWeight: '500', textTransform: 'uppercase' }}>
                                Feedback
                              </p>
                              <p style={{ fontSize: '0.95rem', color: '#fff', margin: 0, lineHeight: '1.6' }}>
                                {feedback.description}
                              </p>
                            </div>
                            {feedback.submitted_at && (
                              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #334155' }}>
                                <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>
                                  📅 Submitted: {new Date(feedback.submitted_at).toLocaleDateString('en-IN', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </UserLayout>
    );
  };

export default Dashboard;

